import type { CSSProperties, FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { type IconName, Icon } from "@mdotm/shadcn/legacy/components";
import { PaletteColors } from "$root/styles/themePalette";
import type { InvestmentStatus } from "@eu.mdotm.api/sphere";

interface TProps {
	border?: CSSProperties["border"];
	backgroundColor?: CSSProperties["backgroundColor"];
	color?: CSSProperties["color"];
	content: {
		status?: InvestmentStatus;
		component?: ReactNode;
	};
}

export const LabelRounded: FC<TProps> = ({ content: propsContent, color, backgroundColor, border }) => {
	const { t } = useTranslation();
	// Temporarily (MDOT-18271) mask Error status as Calculating until BE fixes their process
	const content = propsContent.status === "ERROR" ? { ...propsContent, status: "CALCULATING" as const } : propsContent;

	if (content.status) {
		switch (content.status) {
			case "ACCEPTED":
				backgroundColor = "#DBEFEB";
				content.component = <Icon icon="Icon-Ptf-status-SMALL" color={PaletteColors.GREENY_BLUE} size={15} />;
				break;
			case "READY":
				backgroundColor = "#DBEFEB";
				content.component = <Icon icon="Icon-Ptf-status-SMALL" color={PaletteColors.GREENY_BLUE} size={15} />;
				break;
			case "PROPOSAL_READY":
				backgroundColor = "#E5F6FD";
				content.component = <Icon icon="Icon-full-small" color={PaletteColors.AZURE} size={15} />;
				break;
			case "ERROR":
				backgroundColor = "#FBD9D2";
				content.component = <Icon icon="Close" color={PaletteColors.NEON_RED} size={10} />;
				break;
			case "CALCULATING":
				backgroundColor = "#FFF0CD";
				content.component = (
					<Icon
						icon="calulating"
						color={PaletteColors.YELLOW}
						size={10}
						className={{
							// Temporarily (MDOT-18271) mask Error status as Calculating until BE fixes their process
							"rotate-180": propsContent.status === "ERROR",
						}}
					/>
				);
				break;
			case "REVIEW":
				backgroundColor = "#E5F6FD";
				content.component = <Icon icon="Edit" color={PaletteColors.AZURE} size={15} />;
				break;
			case "RETRIEVING_DATA":
				backgroundColor = "#FFF0CD";
				content.component = <Icon icon="calulating" color={PaletteColors.YELLOW} size={10} />;
				break;
			case "DRAFT":
				backgroundColor = "#E5F6FD";
				content.component = <Icon icon="Edit" color={PaletteColors.AZURE} size={10} />;
				break;
			case "EXECUTING":
				backgroundColor = "#FFF0CD";
				content.component = <Icon icon="calulating" color={PaletteColors.YELLOW} size={10} />;
				break;
		}
	}

	const label = content.status ? t(`PORTFOLIO_STATUS.${content.status}`) : "...";
	return (
		<Container style={{ color, backgroundColor, border }}>
			<WrapperIcon>{content.component}</WrapperIcon>
			{label}
		</Container>
	);
};

function Container({ children, style }: { children: ReactNode; style: React.CSSProperties }): React.JSX.Element {
	return (
		<div
			className="flex items-center m-0 bg-[#e5f6fd] px-[8px] py-[3px] rounded-[20px] font-bold text-[#616161]"
			style={style}
		>
			{children}
		</div>
	);
}

function WrapperIcon({ children }: { children: ReactNode }): React.JSX.Element {
	return <div className="flex mr-[5px]">{children}</div>;
}

export const iconAndColorByInvestmentStatus: Record<InvestmentStatus, { name: IconName; color: string }> = {
	ACCEPTED: {
		name: "Icon-Ptf-status-SMALL",
		color: PaletteColors.GREENY_BLUE, // "#DBEFEB"
	},
	READY: {
		name: "Icon-Ptf-status-SMALL",
		color: PaletteColors.GREENY_BLUE, // "#DBEFEB"
	},
	PROPOSAL_READY: {
		name: "Icon-full-small",
		color: PaletteColors.AZURE, // "#E5F6FD"
	},
	DRAFT: {
		name: "Edit",
		color: PaletteColors.AZURE, // "#E5F6FD"
	},
	ERROR: {
		// Temporarily (MDOT-18271) mask Error status as Calculating until BE fixes their process
		// name: "Close",
		// color: PaletteColors.NEON_RED, // "#FBD9D2"
		name: "calulating",
		color: PaletteColors.YELLOW, // "#FFF0CD"
	},
	CALCULATING: {
		name: "calulating",
		color: PaletteColors.YELLOW, // "#FFF0CD"
	},
	EXECUTING: {
		name: "calulating",
		color: PaletteColors.YELLOW, // "#FFF0CD"
	},
	REVIEW: {
		name: "Edit",
		color: PaletteColors.AZURE, // "#E5F6FD"
	},
	RETRIEVING_DATA: {
		name: "calulating",
		color: PaletteColors.YELLOW, // "#FFF0CD"
	},
};
